import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useGetProgram } from '@interxfi/api-client/program/get-program';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { Button } from '@interxfi/design-system/components/button';
import { type DataDefinition, DefinitionBox } from '@interxfi/design-system/components/definition-box/definition-box';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  type WrappingLayoutColumnDef,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import { PageTabRail } from '../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import { ProgramContactSummary } from '../../../../../../../../../features/userauth/program/contacts/program-contact-summary';
import { ProgramPageHeader } from '../../../../../../../../../features/userauth/program/program-page-header';
import { UpdateProgramContactsDrawer } from '../../../../../../../../../features/userauth/program/update-program-contacts-drawer';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';

export const Route = createFileRoute('/_app/_platform/_context-required/program/_program/$programId/_program/configuration/contacts')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ProgramConfiguration,
      label: 'Contacts',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ProgramConfiguration>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `program-${params.programId}-configuration-contacts`,
        label: 'Configuration',
        to: `/program/${params.programId}/configuration/contacts`,
      }),
    }) satisfies AggregateBreadcrumb,
  component: ProgramContactsConfiguration,
});

const controlDefinitions: DataDefinition[] = [
  {
    id: 'contact',
    definition:
      'A Name, Job Title, Email Address, and Phone may be added for one or more optional Program contacts in each of the business categories.',
  },
];

const rightColumn = { width: { type: 'fixed', width: 320 }, overflow: 'hidden' } satisfies Omit<WrappingLayoutColumnDef, 'render'>;

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load program contacts', "view this program's contact data");

function ProgramContactsConfiguration() {
  const { programId } = Route.useParams();
  const { activeTenantIsPlatform } = useActiveRealmAPIContext();
  const { data, isPending: showPending, error } = useGetProgram({ programId });
  useErrorHandler(error, errorMessageGetter);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <ProgramPageHeader programId={programId} withEntityMenu />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <PageTabRail
          actions={
            activeTenantIsPlatform ? (
              <RequiresAccess write>
                <UpdateProgramContactsDrawer programId={programId} trigger={<Button size="small">Edit</Button>} />
              </RequiresAccess>
            ) : (
              <Tooltip
                activator={
                  <Button disabled size="small">
                    Edit
                  </Button>
                }
                maxWidth={200}
              >
                Please contact the Interchange Team to make changes to your program's contacts.
              </Tooltip>
            )
          }
          group={TabbedNavigationGroup.ProgramConfiguration}
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <ProgramContactSummary contacts={data?.program?.data?.details?.contacts} isPending={showPending} layout="list" />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...rightColumn,
            render: (isWrapping) => (isWrapping ? null : <DefinitionBox definitions={controlDefinitions} />),
          }}
          wrap="forward"
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
